import { useSyncExternalStore } from "react";
import { SMS_PREVIEW_HEADER, decodeSmsHeader, type SmsPreviewPayload } from "@platform/shared-types";

/**
 * Sandbox SMS notices: on a sandbox host the core API sends no real SMS and
 * puts each one (masked number, text, code) on the X-Nevisign-Sms response
 * header instead (base64 of UTF-8 JSON). platform-auth and the signing gateway client hand every response here, and
 * SmsNotice shows them until closed. Production never sets the header.
 */
export type SmsNoticeItem = { id: string; phone: string; message: string; code?: string };

let items: SmsNoticeItem[] = [];
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const recent = new Map<string, number>();

function push(v: SmsPreviewPayload) {
  const key = `${v.phone}|${v.message}`;
  const now = Date.now();
  for (const [k, at] of recent) if (now - at > 15_000) recent.delete(k);
  if (recent.has(key)) return;
  recent.set(key, now);
  items = [
    ...items.slice(-2),
    { id: `${now}-${Math.random().toString(36).slice(2)}`, phone: v.phone, message: v.message, code: v.code },
  ];
  emit();
}

/** A malformed header is ignored; it never breaks the request. */
export function notifySmsFromHeaders(headers: Headers) {
  decodeSmsHeader(headers.get(SMS_PREVIEW_HEADER)).forEach(push);
}

export function dismissSmsNotice(id: string) {
  items = items.filter((i) => i.id !== id);
  emit();
}

export function useSmsNotices() {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => items,
  );
}
